import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { restFetcher } from "~/api/fetchers";

const accessLevel = z.union([z.literal("FULL"), z.literal("LIMITED"), z.literal("RESTRICTED")]);

export type AccessLevel = z.infer<typeof accessLevel>;

export const appointmentSchema = z.object({
  id: z.number(),
  notes: z.string(),
  client: z.object({
    id: z.number(),
    name: z.string(),
    accessLevel,
  }),
  service: z.object({
    name: z.string(),
  }),
  from: z.number(),
  duration: z.number(),
  confirmed: z.boolean(),
  arrived: z.boolean(),
  cancelled: z.boolean(),
  completed: z.boolean(),
  paid: z.boolean(),
});

export type Appointment = z.infer<typeof appointmentSchema>;

const dashboardDataRefetchSettings = {
  staleTime: 10 * 1000, // Cache data for 10 seconds if refocusing window or returning to page
  keepPreviousData: true, // Keep previous data until the new data is fetched
  refetchOnWindowFocus: true, // refetch stale queries when the window regains focus
  refetchOnMount: true, // or the app programmatically returns to the dashboard
};

export const APPOINTMENTS_ENDPOINT = "appointments";

const appointmentsResponse = z.array(appointmentSchema);

export type AppointmentsResponse = z.infer<typeof appointmentsResponse>;

export function useAppointmentsQuery() {
  return useQuery({
    queryKey: [APPOINTMENTS_ENDPOINT],
    queryFn: async () => {
      const resp = await restFetcher<AppointmentsResponse>(`/v1/${APPOINTMENTS_ENDPOINT}`);
      return appointmentsResponse.parse(resp);
    },
    ...dashboardDataRefetchSettings,
  });
}
